import React, { useState, useEffect } from 'react'
import { Eye, ShieldCheck, XCircle, Clock, Package, Copy, TrendingUp, CheckCircle } from 'lucide-react'
import { AdminService } from '../../services/adminService'
import SkinModelReviewModal from './SkinModelReviewModal'

// New Stepper UI for Horizontal tracking
const ConsultationTrack = ({
  type,
  analysisStatus,
  analysisValidationStatus,
  analysisCanViewDetails,
  dermaStatus,
  routineStatus,
  overallStatus,
  processingTime,
  onAnalysisClick,
  onDermaClick,
  onRoutineClick
}) => {
  const getStepState = (status, isRoutine = false) => {
    if (status === 'Pending' || !status) return { style: 'bg-gray-100 text-gray-500 border-gray-200', icon: Clock, label: 'Pending', pulsing: false };
    if (isRoutine) {
      if (status === 'Generated') return { style: 'bg-green-100 text-green-700 border-green-300', icon: Package, label: 'View', pulsing: false, clickable: true };
      return { style: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock, label: status, pulsing: true };
    }
    if (status !== 'Pending') return { style: 'bg-green-100 text-green-700 border-green-300', icon: Eye, label: 'View', pulsing: false, clickable: true };
    return { style: 'bg-gray-100 text-gray-500 border-gray-200', icon: Clock, label: 'Pending', pulsing: false };
  }

  const getAnalysisStepState = () => {
    if (analysisValidationStatus === 'invalid') {
      return {
        style: 'bg-red-100 text-red-700 border-red-300',
        icon: XCircle,
        label: 'Invalid',
        pulsing: false,
        clickable: false
      }
    }

    if (analysisValidationStatus === 'failed') {
      return {
        style: 'bg-red-100 text-red-700 border-red-300',
        icon: XCircle,
        label: 'Failed',
        pulsing: false,
        clickable: false
      }
    }

    if (analysisValidationStatus === 'valid' && !analysisCanViewDetails) {
      return {
        style: 'bg-emerald-100 text-emerald-700 border-emerald-300',
        icon: ShieldCheck,
        label: 'Validated',
        pulsing: false,
        clickable: false
      }
    }

    return getStepState(analysisStatus);
  }

  const analysis = getAnalysisStepState();
  const derma = getStepState(dermaStatus);
  const routine = getStepState(routineStatus, true);

  const StepNode = ({ state, title, onClick }) => {
    const Icon = state.icon

    return (
    <div className="flex flex-col items-center relative z-10 w-16">
      <button
        onClick={state.clickable && onClick ? onClick : undefined}
        disabled={!state.clickable}
        className={`flex items-center justify-center w-8 h-8 rounded-full border-2 bg-white transition-all duration-200 ${state.clickable ? 'cursor-pointer hover:scale-110 shadow-sm hover:shadow-md' : 'cursor-default'
          } ${state.style.includes('green') ? 'border-green-500 text-green-500' :
            state.style.includes('emerald') ? 'border-emerald-500 text-emerald-500' :
              state.style.includes('red') ? 'border-red-500 text-red-500' :
            state.style.includes('yellow') ? 'border-yellow-400 text-yellow-500' : 'border-gray-200 text-gray-400'
          } ${state.pulsing ? 'animate-pulse' : ''}`}
        title={title}
      >
        <Icon className="w-4 h-4" />
      </button>
      <div className="mt-1 text-[9px] font-bold text-gray-400 uppercase tracking-wider">{title}</div>
      <div className={`mt-1 text-[9px] px-2 py-0.5 rounded-full font-medium ${state.style}`}>{state.label}</div>
    </div>
    )
  };

  const Connector = ({ active }) => (
    <div className={`flex-1 h-0.5 mx-[-10px] ${active ? 'bg-green-400' : 'bg-gray-200'} transition-colors duration-300`} />
  );

  return (
    <div className="flex items-center w-full py-2.5 group">
      <div className="w-12 flex-shrink-0 flex flex-col items-center justify-center mr-2">
        <span className={`text-[10px] font-extrabold uppercase tracking-wide ${type === 'Skin' ? 'text-blue-500' :
          type === 'Hair' ? 'text-indigo-500' :
            type === 'Body' ? 'text-pink-500' : 'text-gray-500'
          }`}>{type}</span>
        {processingTime && (
          <span className="text-[9px] text-gray-400 mt-1 whitespace-nowrap font-medium">⏱️ {processingTime}</span>
        )}
      </div>

      <div className="flex bg-gray-50 rounded-xl px-4 py-3 border border-gray-100 flex-1 items-center justify-between relative shadow-sm group-hover:shadow-md group-hover:border-purple-200 group-hover:bg-white transition-all duration-300">
        {type === 'Skin' && (
          <>
            <StepNode state={analysis} title="Analysis" onClick={onAnalysisClick} />
            <Connector active={analysis.clickable} />
          </>
        )}
        <StepNode state={derma} title="Derma" onClick={onDermaClick} />
        <Connector active={derma.clickable} />
        <StepNode state={routine} title="Routine" onClick={onRoutineClick} />

        <div className="ml-4 pl-4 border-l border-gray-200 flex flex-col items-center justify-center w-20 flex-shrink-0">
          <span className="text-[8px] text-gray-400 font-bold uppercase mb-1 tracking-wider">Status</span>
          {overallStatus}
        </div>
      </div>
    </div>
  )
}

const ConsultationsTable = ({ data, loading, onRefresh, userRole }) => {
  const [selectedImage, setSelectedImage] = useState(null)
  const [imageModal, setImageModal] = useState(false)
  const [selectedConsultation, setSelectedConsultation] = useState(null)
  const [outcomeModal, setOutcomeModal] = useState(false)
  const [selectedRoutine, setSelectedRoutine] = useState(null)
  const [selectedRoutineType, setSelectedRoutineType] = useState(null)
  const [selectedBudget, setSelectedBudget] = useState(null)
  const [routineModal, setRoutineModal] = useState(false)
  const [expandedText, setExpandedText] = useState(null)
  const [textModal, setTextModal] = useState(false)
  const [currentImagePath, setCurrentImagePath] = useState(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [analysisModal, setAnalysisModal] = useState(false)
  const [selectedAnalysis, setSelectedAnalysis] = useState(null)
  const [dermaModal, setDermaModal] = useState(false)
  const [selectedDerma, setSelectedDerma] = useState(null)
  const [reviewOverrides, setReviewOverrides] = useState({})
  const [copiedId, setCopiedId] = useState(null)
  const [updateError, setUpdateError] = useState('')

  // Re-assignment modal state
  const [reassignModal, setReassignModal] = useState(false)
  const [selectedConsultationForReassign, setSelectedConsultationForReassign] = useState(null)
  const [availablePharmacists, setAvailablePharmacists] = useState([])
  const [selectedPharmacist, setSelectedPharmacist] = useState('')
  const [reassignLoading, setReassignLoading] = useState(false)
  const [reassignError, setReassignError] = useState('')
  const [reassignSessionWarning, setReassignSessionWarning] = useState('')

  // Pending queue status tracking
  const [pendingStatusMap, setPendingStatusMap] = useState({})

  // Track selected profile tag tab per row
  const [activeProfileTabs, setActiveProfileTabs] = useState({})

  // Fetch pending status when data changes
  useEffect(() => {
    const fetchPendingStatus = async () => {
      if (!data || data.length === 0) return

      const userIds = data.map(item => item.user_id).filter(Boolean)
      if (userIds.length === 0) return

      const result = await AdminService.getPendingStatusForUsers(userIds)
      if (result.success) {
        setPendingStatusMap(result.data)
      }
    }

    fetchPendingStatus()
  }, [data])

  // Check user roles
  const isSuperAdmin = userRole === 'super_admin'
  const isCoordinator = userRole === 'coordinator'
  // Coordinators and super admins see all data, including "Assigned To" column
  const canSeeAllData = isSuperAdmin || isCoordinator
  // Only super_admin and pharmacists can set outcomes (not coordinators - read-only)
  const canSetOutcome = userRole === 'super_admin' || userRole === 'pharmacist'

  // Helper function to parse Derma JSON
  const parseDermaOutput = (dermaString) => {
    try {
      // Try to parse as JSON
      const parsed = JSON.parse(
        typeof dermaString === 'string'
          ? dermaString.trim().replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/\s*```$/i, '')
          : dermaString
      )
      return parsed
    } catch (e) {
      // If it's not JSON, return as-is
      return null
    }
  }

  const openSkinDermaModal = (item) => {
    if (!isSuperAdmin) {
      setSelectedDerma({ type: 'skin_target_only', output: item.derma_output })
      setDermaModal(true)
      return
    }

    const reviewOverride = item?.consultation_id ? reviewOverrides[item.consultation_id] : null

    setSelectedDerma({
      type: 'skin',
      consultation: {
        ...item,
        model_output_review: reviewOverride || item.model_output_review || null
      }
    })
    setDermaModal(true)
  }

  const handleReviewSaved = (consultationId, nextReview) => {
    if (!consultationId) {
      return
    }

    setReviewOverrides((currentOverrides) => ({
      ...currentOverrides,
      [consultationId]: nextReview
    }))

    setSelectedDerma((currentSelection) => {
      if (
        !currentSelection ||
        currentSelection.type !== 'skin' ||
        currentSelection.consultation?.consultation_id !== consultationId
      ) {
        return currentSelection
      }

      return {
        ...currentSelection,
        consultation: {
          ...currentSelection.consultation,
          model_output_review: nextReview
        }
      }
    })
  }

  // Helper function to copy user ID
  const copyUserId = (userId) => {
    navigator.clipboard.writeText(userId)
    setCopiedId(userId)
    setTimeout(() => setCopiedId(null), 2000)
  }

  // Helper function to determine user source
  const getUserSource = (sessionId) => {
    if (!sessionId) return { label: 'Unknown', color: 'bg-gray-100 text-gray-800' }
    if (sessionId.toLowerCase().startsWith('gsheet')) {
      return { label: 'Self-Care', color: 'bg-purple-100 text-purple-800' }
    }
    return { label: 'Form', color: 'bg-blue-100 text-blue-800' }
  }

  // Consultation status options
  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' },
    { value: 'wondura_done', label: 'Ready', color: 'bg-blue-100 text-blue-800' },
    { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' }
  ]

  // Outcome options
  const outcomeOptions = [
    { value: 'paid', label: 'PAID', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    { value: 'hot_lead', label: 'HOT LEAD', color: 'bg-orange-100 text-orange-800', icon: TrendingUp },
    { value: 'cold_lead', label: 'COLD LEAD', color: 'bg-gray-100 text-gray-800', icon: XCircle }
  ]

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find(s => s.value === status) ||
      { label: status, color: 'bg-gray-100 text-gray-800' }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    )
  }

  // Get outcome badge
  const getOutcomeBadge = (outcome) => {
    if (!outcome) return <span className="text-gray-400 text-xs">N/A</span>

    const outcomeConfig = outcomeOptions.find(o => o.value === outcome) ||
      { label: outcome, color: 'bg-gray-100 text-gray-800' }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${outcomeConfig.color}`}>
        {outcomeConfig.label}
      </span>
    )
  }

  // Handle image view with auto-refresh capability
  const handleViewImage = async (imagePath) => {
    if (!imagePath) return

    setImageLoading(true)
    try {
      // console.log('🔍 Handling image path:', imagePath)

      const imageUrl = await AdminService.getImageUrl(imagePath)
      if (imageUrl) {
        setSelectedImage(imageUrl)
        setCurrentImagePath(imagePath)
        setImageModal(true)
        console.log('✅ Image loaded successfully with 10min expiry')
      } else {
        console.error('❌ Failed to get image URL')
        alert('Failed to load image. Please check if the image exists in storage.')
      }
    } catch (error) {
      console.error('Error loading image:', error)
      alert('Error loading image: ' + error.message)
    } finally {
      setImageLoading(false)
    }
  }

  // Refresh expired image URL
  const handleRefreshImage = async () => {
    if (!currentImagePath) return

    setImageLoading(true)
    try {
      console.log('🔄 Refreshing expired image URL...')
      const newImageUrl = await AdminService.getImageUrl(currentImagePath)
      if (newImageUrl) {
        setSelectedImage(newImageUrl)
        console.log('✅ Image URL refreshed successfully')
      } else {
        alert('Failed to refresh image URL')
      }
    } catch (error) {
      console.error('Error refreshing image:', error)
      alert('Error refreshing image: ' + error.message)
    } finally {
      setImageLoading(false)
    }
  }

  // Handle outcome update (PAID/HOT_LEAD/COLD_LEAD)
  const handleOutcomeUpdate = async (consultationId, outcome) => {
    setUpdateError('')
    try {
      const result = await AdminService.updateConsultationOutcome(consultationId, outcome)
      if (result.success) {
        setOutcomeModal(false)
        setSelectedConsultation(null)
        onRefresh() // Refresh the dashboard data
      } else {
        const errorMsg = result.error || 'Failed to update consultation outcome'
        setUpdateError(errorMsg)
        alert(errorMsg)
      }
    } catch (error) {
      console.error('Outcome update error:', error)
      const errorMsg = 'Error updating outcome'
      setUpdateError(errorMsg)
      alert(errorMsg)
    }
  }

  // Show outcome selection modal
  const showOutcomeModal = (consultation) => {
    setSelectedConsultation(consultation)
    setOutcomeModal(true)
  }

  // Load pharmacists when modal opens
  const loadPharmacists = async () => {
    const result = await AdminService.getAllPharmacists()
    if (result.success) {
      setAvailablePharmacists(result.data || [])
    } else {
      setReassignError('Failed to load pharmacists')
    }
  }

  const checkReassignSession = async () => {
    const isSessionHealthy = await AdminService.verifySession({ logoutOnFailure: false })

    if (isSessionHealthy) {
      setReassignSessionWarning('')
      return true
    }

    setReassignSessionWarning('Your session appears to be expired. You can still try this action, but if Supabase rejects it you will need to log in again.')
    return false
  }

  const openReassignModal = (consultation) => {
    setSelectedConsultationForReassign(consultation)
    setReassignModal(true)
    setSelectedPharmacist('')
    setReassignError('')
    setReassignSessionWarning('')
    loadPharmacists()
    checkReassignSession()
  }

  // Handle re-assignment
  const handleReassign = async () => {
    if (!selectedPharmacist) {
      setReassignError('Please select a pharmacist')
      return
    }

    setReassignLoading(true)
    setReassignError('')
    await checkReassignSession()

    const result = await AdminService.reassignConsultation(
      selectedConsultationForReassign.consultation_id,
      selectedPharmacist
    )

    if (result.warning) {
      setReassignSessionWarning(result.warning)
    }

    if (result.success) {
      setReassignModal(false)
      setSelectedConsultationForReassign(null)
      setSelectedPharmacist('')
      setReassignSessionWarning('')
      onRefresh() // Refresh table data
      alert(result.message || 'Successfully reassigned')
    } else {
      setReassignError(result.error || 'Failed to reassign consultation')
    }

    setReassignLoading(false)
  }

  // Truncate long text with click to expand
  // eslint-disable-next-line no-unused-vars
  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'N/A'
    if (text.length <= maxLength) return text

    return (
      <span>
        {text.substring(0, maxLength)}...
        <button
          onClick={() => {
            setExpandedText(text)
            setTextModal(true)
          }}
          className="ml-1 text-blue-600 hover:text-blue-800 text-xs underline"
        >
          Read more
        </button>
      </span>
    )
  }

  // Handle routine view
  const handleViewRoutine = (products, type = 'skin', budget = null) => {
    let parsedRoutine = products;
    if (typeof products === 'string') {
      try {
        parsedRoutine = JSON.parse(products);
      } catch (e) {
        console.error("Error parsing routine JSON:", e);
      }
    }
    setSelectedRoutine(parsedRoutine)
    setSelectedRoutineType(type)
    setSelectedBudget(budget)
    setRoutineModal(true)
  }

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading dashboard data...</p>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="p-8 text-center">
        <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-600">No consultation data found</p>
      </div>
    )
  }

  return (
    <>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: canSeeAllData ? '13%' : '15%' }}>
                User Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: canSeeAllData ? '13%' : '15%' }}>
                Profile Tags
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '5%' }}>
                📷
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: canSeeAllData ? '40%' : '48%' }}>
                Consultation Journey
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: canSeeAllData ? '10%' : '12%' }}>
                Outcome
              </th>
              {canSeeAllData && (
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                  Assigned To
                </th>
              )}
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: canSeeAllData ? '10%' : '12%' }}>
                Updated
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item) => (
              <tr key={item.consultation_id || item.user_id} className="hover:bg-gray-50">
                {/* User Info */}
                <td className="px-6 py-4">
                  <div className="text-sm space-y-1.5 flex flex-col items-center">
                    <button
                      onClick={() => copyUserId(item.user_id)}
                      className="font-medium text-gray-900 hover:text-[#A73A5D] flex items-center gap-1 transition-colors group"
                      title="Click to copy User ID"
                    >
                      <span>{item.name}</span>
                      <Copy className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${copiedId === item.user_id ? 'text-green-600' : ''}`} />
                    </button>
                    <div className="text-gray-500 text-center">{item.phone}</div>
                    {item.contact_id && (
                      <div className="text-xs text-gray-600 text-center">
                        <span className="font-semibold">Contact ID:</span> {item.contact_id}
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                      {item.session_id && (
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${getUserSource(item.session_id).color}`}>
                          {getUserSource(item.session_id).label}
                        </span>
                      )}
                      {item.is_anonymous ? (
                        <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                          👤 Anonymous
                        </span>
                      ) : (
                        <a
                          href={item.contact_id ? `https://app.respond.io/space/173728/inbox/${item.contact_id}` : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-block px-2 py-0.5 text-xs rounded-full transition-colors ${item.contact_id
                            ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer'
                            : 'bg-green-100 text-green-700 cursor-not-allowed opacity-50'
                            }`}
                          onClick={(e) => {
                            if (!item.contact_id) {
                              e.preventDefault()
                              alert('Contact ID not available')
                            }
                          }}
                          title={item.contact_id ? `Open in Respond.io: ${item.contact_id}` : 'Contact ID not available'}
                        >
                          ✓ Registered
                        </a>
                      )}
                    </div>
                    {/* Consultation Types Display */}
                    {item.consultation_types && item.consultation_types.length > 0 && (
                      <div className="flex flex-col items-center mt-2 w-full pt-2 border-t border-gray-100">
                        <span className="text-[10px] text-gray-400 font-semibold mb-1 uppercase tracking-wider">Consultations</span>
                        <div className="flex flex-wrap items-center justify-center gap-1">
                          {item.consultation_types.map(t => (
                            <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded-md border border-purple-100">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </td>

                {/* Profile Tags - Consolidated Dropdown Column */}
                <td className="px-6 py-4">
                  {(() => {
                    const types = item.consultation_types || ['Skin'];
                    const tabKey = item.consultation_id || item.user_id;
                    const activeTab = activeProfileTabs[tabKey] || types[0] || 'Skin';

                    return (
                      <div className="flex flex-col gap-2">
                        {/* Dropdown for consultation type selection */}
                        {types.length > 0 && (
                          <div className="relative w-full mb-1">
                            <select
                              value={activeTab}
                              onChange={(e) => setActiveProfileTabs(prev => ({ ...prev, [tabKey]: e.target.value }))}
                              className="w-full text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700 py-1.5 px-2 pl-3 pr-8 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 appearance-none shadow-sm cursor-pointer"
                              style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.5rem center',
                                backgroundSize: '1em 1em'
                              }}
                            >
                              {types.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Content based on selected tab */}
                        <div className="flex flex-col gap-1.5">
                          {activeTab === 'Skin' && (
                            <>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full inline-block w-fit">
                                {item.skin_type || 'Not specified'}
                              </span>
                              <span className="px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded-full inline-block w-fit">
                                {item.relationship_status || 'Not specified'}
                              </span>
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full inline-block w-fit">
                                {item.budget || 'Not specified'}
                              </span>
                              {item.sensitivity_details && item.sensitivity_details !== 'None' && (
                                <div className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md">
                                  <div className="font-semibold mb-0.5">⚠️ Sensitivity:</div>
                                  <div className="text-xs leading-relaxed">{item.sensitivity_details}</div>
                                </div>
                              )}
                            </>
                          )}

                          {activeTab === 'Hair' && (
                            <>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full inline-block w-fit">
                                {item.hair_type || 'Not specified'}
                              </span>
                              <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full inline-block w-fit">
                                Texture: {item.hair_texture || 'Not specified'}
                              </span>
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full inline-block w-fit">
                                Prob: {item.hair_problem || 'Not specified'}
                              </span>
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full inline-block w-fit">
                                {item.hair_budget || 'Not specified'}
                              </span>
                              {item.hair_any_issues && (
                                <div className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md">
                                  <div className="font-semibold mb-0.5">⚠️ Issues:</div>
                                  <div className="text-xs leading-relaxed">{item.hair_any_issues}</div>
                                </div>
                              )}
                            </>
                          )}

                          {activeTab === 'Body' && (
                            <>
                              <span className="px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded-full inline-block w-fit">
                                {item.body_relationship_status || 'Not specified'}
                              </span>
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full inline-block w-fit">
                                {item.body_budget || 'Not specified'}
                              </span>
                              {item.body_concerns && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md inline-block max-w-full whitespace-normal break-words" title={item.body_concerns}>
                                  {item.body_concerns}
                                </span>
                              )}
                              {item.body_sensitivity_details && item.body_sensitivity_details !== 'None' && (
                                <div className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md">
                                  <div className="font-semibold mb-0.5">⚠️ Sensitivity:</div>
                                  <div className="text-xs leading-relaxed whitespace-normal break-words">{item.body_sensitivity_details}</div>
                                </div>
                              )}
                            </>
                          )}

                          {(activeTab === 'Makeup' || activeTab === 'Perfume') && (
                            <>
                              <span className="px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded-full inline-block w-fit">
                                {item.relationship_status || 'Not specified'}
                              </span>
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full inline-block w-fit">
                                {item.budget || 'Not specified'}
                              </span>
                              {item.sensitivity_details && item.sensitivity_details !== 'None' && (
                                <div className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md">
                                  <div className="font-semibold mb-0.5">⚠️ Full Sensitivity:</div>
                                  <div className="text-xs leading-relaxed">{item.sensitivity_details}</div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </td>

                {/* Photo Link - Just Icon */}
                <td className="px-3 py-4 whitespace-nowrap text-center">
                  {item.photo_link ? (
                    (() => {
                      const isImageInvalid = item.image_validation_status === 'invalid' ||
                        (item.hair_analysis && typeof item.hair_analysis === 'string' && item.hair_analysis.toLowerCase().includes('not valid'));
                      return (
                        <button
                          onClick={() => handleViewImage(item.photo_link)}
                          className={`inline-flex items-center justify-center p-1 rounded transition-colors ${isImageInvalid
                            ? 'text-red-600 bg-red-50 border-2 border-red-500 hover:bg-red-100 shadow-sm shadow-red-100'
                            : 'text-purple-600 hover:text-purple-900 border-2 border-transparent'
                            }`}
                          title={isImageInvalid ? "Image Not Valid" : "View image"}
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      );
                    })()
                  ) : (
                    <span
                      className="text-gray-300 inline-flex items-center justify-center p-1 border-2 border-transparent"
                      title="No image"
                    >
                      <Eye className="w-5 h-5" />
                    </span>
                  )}
                </td>

                {/* Consultation Journey - Unified Horizontal Stepper Timeline */}
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-1">
                    {/* Skin Track */}
                    {(item.consultation_types?.includes('Skin') || (!item.has_hair_consultation && !item.has_body_consultation)) && (
                      <ConsultationTrack
                        type="Skin"
                        analysisStatus={item.image_analysis}
                        analysisValidationStatus={item.image_validation_status}
                        analysisCanViewDetails={item.image_analysis_details_available}
                        dermaStatus={item.derma_output}
                        routineStatus={item.routine_output}
                        overallStatus={getStatusBadge(item.consultation_status)}
                        processingTime={item.processing_time}
                        onAnalysisClick={() => {
                          if (!item.image_analysis_details_available) {
                            return
                          }
                          setSelectedAnalysis(item.image_analysis)
                          setAnalysisModal(true)
                        }}
                        onDermaClick={() => openSkinDermaModal(item)}
                        onRoutineClick={() => handleViewRoutine(item.routine_products, 'skin', item.budget)}
                      />
                    )}

                    {/* Hair Track */}
                    {item.has_hair_consultation && (
                      <ConsultationTrack
                        type="Hair"
                        analysisStatus={item.hair_analysis}
                        dermaStatus={item.hair_derma_output}
                        routineStatus={item.hair_routine_output}
                        overallStatus={getStatusBadge(item.hair_consultation_status)}
                        processingTime={item.hair_processing_time}
                        onAnalysisClick={() => {
                          setSelectedAnalysis(item.hair_analysis)
                          setAnalysisModal(true)
                        }}
                        onDermaClick={() => {
                          setSelectedDerma({ type: 'hair', output: item.hair_derma_output })
                          setDermaModal(true)
                        }}
                        onRoutineClick={() => handleViewRoutine(item.hair_routine_products, 'hair', item.hair_budget)}
                      />
                    )}

                    {/* Body Track */}
                    {item.has_body_consultation && (
                      <ConsultationTrack
                        type="Body"
                        analysisStatus={item.body_analysis}
                        dermaStatus={item.body_derma_output}
                        routineStatus={item.body_routine_output}
                        overallStatus={getStatusBadge(item.body_consultation_status || 'pending')}
                        processingTime={item.body_processing_time}
                        onAnalysisClick={() => {
                          setSelectedAnalysis(item.body_analysis)
                          setAnalysisModal(true)
                        }}
                        onDermaClick={() => {
                          setSelectedDerma({ type: 'body', output: item.body_derma_output })
                          setDermaModal(true)
                        }}
                        onRoutineClick={() => handleViewRoutine(item.body_routine_products, 'body', item.body_budget)}
                      />
                    )}
                  </div>
                </td>

                {/* Consultation Outcome */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col items-start gap-2">
                    {item.consultation_outcome ? (
                      getOutcomeBadge(item.consultation_outcome)
                    ) : item.consultation_status === 'wondura_done' ? (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                        Ready
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">No outcome</span>
                    )}

                    {canSetOutcome && (
                      <button
                        onClick={() => showOutcomeModal(item)}
                        className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-xs font-medium"
                      >
                        {item.consultation_outcome ? 'Change Outcome' : 'Set Outcome'}
                      </button>
                    )}
                  </div>
                </td>

                {/* Assigned To - Super Admin and Coordinator */}
                {canSeeAllData && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="space-y-1">
                      {/* Check if user is in pending queue */}
                      {(() => {
                        const pendingStatus = pendingStatusMap[item.user_id]
                        const isInQueue = pendingStatus?.status === 'queued'

                        if (isInQueue) {
                          // Show "In Queue" badge
                          return (
                            <div className="flex flex-col items-center gap-1">
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium animate-pulse">
                                In Queue
                              </span>
                              <span className="text-xs text-gray-500">
                                Since {new Date(pendingStatus.queued_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )
                        }

                        // Current Assignment (normal flow)
                        if (item.assigned_pharmacist) {
                          return (
                            <div className="flex flex-col items-center gap-1">
                              <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                                {item.assigned_pharmacist}
                              </span>

                              {/* Re-assign Button - Super Admin and Coordinator */}
                              {(isSuperAdmin || isCoordinator) && (
                                <button
                                  onClick={() => openReassignModal(item)}
                                  className="text-xs text-purple-600 hover:text-purple-800 underline"
                                >
                                  Re-assign
                                </button>
                              )}

                              {/* Show "Changed by" if reassigned */}
                              {item.assignment_changed && item.reassigned_at && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Changed {new Date(item.reassigned_at).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          )
                        }

                        // Unassigned
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-xs text-gray-400">Unassigned</span>

                            {/* Assign Button - Super Admin and Coordinator (for unassigned consultations) */}
                            {(isSuperAdmin || isCoordinator) && (
                              <button
                                onClick={() => openReassignModal(item)}
                                className="text-xs text-green-600 hover:text-green-800 underline"
                              >
                                Assign
                              </button>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  </td>
                )}

                {/* Updated At */}
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="whitespace-nowrap text-center">
                    {item.updated_at}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table >
      </div >

      {/* Image Modal */}
      {
        imageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-auto">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-medium">Consultation Image</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleRefreshImage}
                    disabled={imageLoading}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {imageLoading ? 'Refreshing...' : '🔄 Refresh URL'}
                  </button>
                  <button
                    onClick={() => {
                      setImageModal(false)
                      setSelectedImage(null)
                      setCurrentImagePath(null)
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="p-4">
                {imageLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading image...</span>
                  </div>
                )}
                {selectedImage && !imageLoading && (
                  <div className="space-y-4">
                    <img
                      src={selectedImage}
                      alt="Consultation"
                      className="max-w-full h-auto rounded-lg shadow-lg"
                      onError={(e) => {
                        console.error('❌ Image failed to load, URL may be expired')
                        e.target.style.display = 'none'
                        // Show refresh message
                        const errorDiv = document.createElement('div')
                        errorDiv.innerHTML = `
                        <div class="text-center py-8 text-gray-600">
                          <p class="mb-2">⚠️ Image failed to load (URL may have expired)</p>
                          <p class="text-sm">Click the "Refresh URL" button above to generate a new link</p>
                        </div>
                      `
                        e.target.parentNode.appendChild(errorDiv)
                      }}
                    />
                    <div className="text-center text-xs text-gray-500">
                      ⏰ This image URL expires in 10 minutes. Click "Refresh URL" if the image stops loading.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Text Expansion Modal */}
      {
        textModal && expandedText && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl max-h-96 overflow-auto">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-medium">Full Text</h3>
                <button
                  onClick={() => {
                    setTextModal(false)
                    setExpandedText(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{expandedText}</p>
              </div>
            </div>
          </div>
        )
      }

      {/* Routine Products Modal */}
      {
        routineModal && selectedRoutine && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-7xl max-h-[95vh] overflow-auto w-full shadow-2xl">
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 flex justify-between items-center z-10">
                <h3 className="text-xl font-bold text-white">
                  {selectedRoutineType === 'hair' ? 'Hair Care Routine' : selectedRoutineType === 'body' ? 'Body Care Routine' : 'Skincare Routine'}
                </h3>
                <button
                  onClick={() => {
                    setRoutineModal(false)
                    setSelectedRoutine(null)
                    setSelectedRoutineType(null)
                  }}
                  className="text-white hover:text-gray-200 text-3xl font-light transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-8 bg-gradient-to-b from-gray-50 to-white">
                {/* Prepare routine data based on type */}
                {(() => {
                  // If it's hair, checks if it's wrapped in checkout_page
                  // If it's skin, it's usually inside checkout_page (current structure of selectedRoutine)
                  console.log('Rendering modal for type:', selectedRoutineType);

                  // Normalize routine payload shapes:
                  // - { checkout_page: {...} }
                  // - { products_offered: { checkout_page: {...} } }
                  // - [{ products_offered: { checkout_page: {...} } }]
                  const normalizeRoutineItems = (items) => {
                    if (Array.isArray(items)) return items;
                    if (items && typeof items === 'object') {
                      return Object.values(items).filter(
                        (item) => item && typeof item === 'object'
                      );
                    }
                    return [];
                  };

                  let routineData = selectedRoutine;

                  if (Array.isArray(routineData)) {
                    routineData = routineData.length > 0 ? routineData[0] : {};
                  }

                  if (routineData?.products_offered) {
                    routineData = routineData.products_offered;
                  }

                  if (routineData?.checkout_page) {
                    routineData = routineData.checkout_page;
                  }

                  if (selectedRoutineType === 'hair') {
                    routineData = {
                      ...routineData,
                      pre_shower_routine: normalizeRoutineItems(routineData?.pre_shower_routine),
                      shower_routine: normalizeRoutineItems(routineData?.shower_routine),
                      post_shower_routine: normalizeRoutineItems(routineData?.post_shower_routine),
                      weekly_treatments: normalizeRoutineItems(
                        routineData?.weekly_treatments || routineData?.weekly_treatment
                      ),
                    };
                  }

                  return (
                    <>
                      {/* Skin Routine - checkout_page structure (Morning/Evening) */}
                      {(selectedRoutineType === 'skin' || (routineData.morning_routine || routineData.evening_routine)) && (
                        <>
                          {/* Morning Routine */}
                          {routineData.morning_routine && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 pb-3 border-b-2 border-orange-200">
                                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-yellow-500 rounded-full flex items-center justify-center">
                                  <span className="text-xl">🌅</span>
                                </div>
                                <h4 className="text-xl font-bold text-gray-800">Morning Routine</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {routineData.morning_routine.map((product, index) => (
                                  <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-purple-300 transition-all duration-300">
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
                                        {product.step}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-bold text-base text-gray-900 mb-1 leading-tight">{product.title}</p>
                                        <p className="text-sm text-gray-700 mb-2 leading-snug">{product.product_name}</p>
                                        <div className="inline-block bg-green-100 px-3 py-1 rounded-md">
                                          <p className="text-base font-bold text-green-700">{product.price_iqd} IQD</p>
                                        </div>
                                      </div>
                                    </div>
                                    {product.image?.url && (
                                      <img
                                        src={product.image.url}
                                        alt={product.title}
                                        className="w-full max-h-32 object-contain rounded-lg mt-3 bg-gray-50"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        </>
                      )}

                      {/* Evening Routine */}
                      {routineData.evening_routine && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 pb-3 border-b-2 border-indigo-200">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-xl">🌙</span>
                            </div>
                            <h4 className="text-xl font-bold text-gray-800">Evening Routine</h4>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {routineData.evening_routine.map((product, index) => (
                              <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-indigo-300 transition-all duration-300">
                                <div className="flex items-start gap-3 mb-3">
                                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
                                    {product.step}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-bold text-base text-gray-900 mb-1 leading-tight">{product.title}</p>
                                    <p className="text-sm text-gray-700 mb-2 leading-snug">{product.product_name}</p>
                                    <div className="inline-block bg-green-100 px-3 py-1 rounded-md">
                                      <p className="text-base font-bold text-green-700">{product.price_iqd} IQD</p>
                                    </div>
                                  </div>
                                </div>
                                {product.image?.url && (
                                  <img
                                    src={product.image.url}
                                    alt={product.title}
                                    className="w-full max-h-32 object-contain rounded-lg mt-3 bg-gray-50"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Total Price */}
                      {routineData.total_price_iqd && (
                        <div className="border-t-2 border-gray-300 pt-4 sticky bottom-0 bg-white">
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200">
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-semibold text-gray-700">Grand Total:</span>
                              <p className="text-2xl font-bold text-green-700">
                                {routineData.total_price_iqd} IQD
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Body Routine - flat routine array from checkout_page */}
                      {selectedRoutineType === 'body' && routineData.routine && routineData.routine.length > 0 && (
                        <>
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 pb-3 border-b-2 border-pink-200">
                              <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center">
                                <span className="text-xl">🧴</span>
                              </div>
                              <h4 className="text-xl font-bold text-gray-800">Body Care Routine</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                              {routineData.routine.map((product, index) => (
                                <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-pink-300 transition-all duration-300">
                                  <div className="flex items-start gap-3 mb-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
                                      {product.step || index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-bold text-base text-gray-900 mb-1 leading-tight">{product.title}</p>
                                      <p className="text-sm text-gray-700 mb-2 leading-snug">{product.product_name}</p>
                                      <div className="inline-block bg-green-100 px-3 py-1 rounded-md">
                                        <p className="text-base font-bold text-green-700">{product.price_iqd} IQD</p>
                                      </div>
                                    </div>
                                  </div>
                                  {product.image?.url && (
                                    <img
                                      src={product.image.url}
                                      alt={product.title}
                                      className="w-full max-h-32 object-contain rounded-lg mt-3 bg-gray-50"
                                    />
                                  )}
                                  {product.link && (
                                    <a
                                      href={product.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mt-2 inline-flex items-center text-xs text-pink-600 hover:text-pink-800 font-medium"
                                    >
                                      View Product →
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Body Total Price */}
                          {routineData.total_price_iqd && (
                            <div className="border-t-2 border-gray-300 pt-4 sticky bottom-0 bg-white">
                              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200">
                                <div className="flex justify-between items-center">
                                  <span className="text-lg font-semibold text-gray-700">Grand Total:</span>
                                  <p className="text-2xl font-bold text-green-700">
                                    {routineData.total_price_iqd} IQD
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Hair Routine - Direct structure (pre_shower_routine, etc.) OR Inside checkout_page */}
                      {(selectedRoutineType === 'hair' || (routineData.pre_shower_routine || routineData.shower_routine)) && (
                        <>
                          {/* Pre-Shower Routine */}
                          {routineData.pre_shower_routine && routineData.pre_shower_routine.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 pb-3 border-b-2 border-amber-200">
                                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                                  <span className="text-xl">🛢️</span>
                                </div>
                                <h4 className="text-xl font-bold text-gray-800">Pre-Shower Routine</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {routineData.pre_shower_routine.map((product, index) => (
                                  <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-amber-300 transition-all duration-300">
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0">
                                        {index + 1}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-bold text-base text-gray-900 mb-1 leading-tight">{product.title}</p>
                                        <p className="text-sm text-gray-700 mb-2 leading-snug">{product.product_name}</p>
                                        <div className="inline-block bg-green-100 px-3 py-1 rounded-md">
                                          <p className="text-base font-bold text-green-700">{product.price_iqd} IQD</p>
                                        </div>
                                      </div>
                                    </div>
                                    {product.image?.url && (
                                      <img
                                        src={product.image.url}
                                        alt={product.title}
                                        className="w-full max-h-32 object-contain rounded-lg mt-3 bg-gray-50"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Shower Routine */}
                          {routineData.shower_routine && routineData.shower_routine.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 pb-3 border-b-2 border-cyan-200">
                                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                                  <span className="text-xl">🚿</span>
                                </div>
                                <h4 className="text-xl font-bold text-gray-800">Shower Routine</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {routineData.shower_routine.map((product, index) => (
                                  <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-cyan-300 transition-all duration-300">
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0">
                                        {index + 1}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-bold text-base text-gray-900 mb-1 leading-tight">{product.title}</p>
                                        <p className="text-sm text-gray-700 mb-2 leading-snug">{product.product_name}</p>
                                        <div className="inline-block bg-green-100 px-3 py-1 rounded-md">
                                          <p className="text-base font-bold text-green-700">{product.price_iqd} IQD</p>
                                        </div>
                                      </div>
                                    </div>
                                    {product.image?.url && (
                                      <img
                                        src={product.image.url}
                                        alt={product.title}
                                        className="w-full max-h-32 object-contain rounded-lg mt-3 bg-gray-50"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Post-Shower Routine */}
                          {routineData.post_shower_routine && routineData.post_shower_routine.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 pb-3 border-b-2 border-rose-200">
                                <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center">
                                  <span className="text-xl">✨</span>
                                </div>
                                <h4 className="text-xl font-bold text-gray-800">Post-Shower Routine</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {routineData.post_shower_routine.map((product, index) => (
                                  <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-rose-400 transition-all duration-300">
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0">
                                        {index + 1}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-bold text-base text-gray-900 mb-1 leading-tight">{product.title}</p>
                                        <p className="text-sm text-gray-700 mb-2 leading-snug">{product.product_name}</p>
                                        <div className="inline-block bg-green-100 px-3 py-1 rounded-md">
                                          <p className="text-base font-bold text-green-700">{product.price_iqd} IQD</p>
                                        </div>
                                      </div>
                                    </div>
                                    {product.image?.url && (
                                      <img
                                        src={product.image.url}
                                        alt={product.title}
                                        className="w-full max-h-32 object-contain rounded-lg mt-3 bg-gray-50"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Weekly Treatments */}
                          {routineData.weekly_treatments && routineData.weekly_treatments.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 pb-3 border-b-2 border-purple-200">
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-violet-500 rounded-full flex items-center justify-center">
                                  <span className="text-xl">🌟</span>
                                </div>
                                <h4 className="text-xl font-bold text-gray-800">Weekly Treatments</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {routineData.weekly_treatments.map((product, index) => (
                                  <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-purple-300 transition-all duration-300">
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0">
                                        {index + 1}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-bold text-base text-gray-900 mb-1 leading-tight">{product.title}</p>
                                        <p className="text-sm text-gray-700 mb-2 leading-snug">{product.product_name}</p>
                                        <div className="inline-block bg-green-100 px-3 py-1 rounded-md">
                                          <p className="text-base font-bold text-green-700">{product.price_iqd} IQD</p>
                                        </div>
                                      </div>
                                    </div>
                                    {product.image?.url && (
                                      <img
                                        src={product.image.url}
                                        alt={product.title}
                                        className="w-full max-h-32 object-contain rounded-lg mt-3 bg-gray-50"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Budget Summary for Hair */}
                          {/* Budget Summary */}
                          {(() => {
                            const getBudgetInfo = () => {
                              if (selectedRoutine.budget_summary) return selectedRoutine.budget_summary;

                              if (routineData.total_price_iqd) {
                                const uniqueItems =
                                  (routineData.pre_shower_routine?.length || 0) +
                                  (routineData.shower_routine?.length || 0) +
                                  (routineData.post_shower_routine?.length || 0) +
                                  (routineData.weekly_treatments?.length || 0) +
                                  (routineData.routine?.length || 0);

                                let withinBudget = null;
                                if (selectedBudget) {
                                  // Try to extract max budget from string like "65-85K IQD"
                                  const maxMatch = selectedBudget.match(/-(\d+)K/i) || selectedBudget.match(/(\d+)K/i);
                                  if (maxMatch && maxMatch[1]) {
                                    const maxLimit = parseInt(maxMatch[1]) * 1000;
                                    withinBudget = routineData.total_price_iqd <= maxLimit;
                                  }
                                }

                                return {
                                  unique_items: uniqueItems,
                                  total_cost: routineData.total_price_iqd,
                                  budget_tier: selectedBudget || "Not specified",
                                  within_budget: withinBudget
                                };
                              }
                              return null;
                            };

                            const summary = getBudgetInfo();

                            if (!summary) return null;

                            return (
                              <div className="border-t-2 border-gray-300 pt-4 sticky bottom-0 bg-white">
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200">
                                  <div className="flex justify-between items-center mb-3">
                                    <span className="text-lg font-semibold text-gray-700">Budget Summary:</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-white rounded-lg p-3 border border-green-200">
                                      <p className="text-sm text-gray-600">Unique Items</p>
                                      <p className="text-xl font-bold text-green-700">{summary.unique_items}</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-green-200">
                                      <p className="text-sm text-gray-600">Total Cost</p>
                                      <p className="text-xl font-bold text-green-700">{summary.total_cost?.toLocaleString()} IQD</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-green-200">
                                      <p className="text-sm text-gray-600">Budget Tier</p>
                                      <p className="text-xl font-bold text-green-700">{summary.budget_tier}</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-green-200">
                                      <p className="text-sm text-gray-600">Within Budget</p>
                                      <p className="text-xl font-bold text-green-700">
                                        {summary.within_budget === true ? '✅ Yes' :
                                          summary.within_budget === false ? '⚠️ No' : 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div >
        )
      }

      {/* Analysis Modal */}
      {
        analysisModal && selectedAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-3xl max-h-[80vh] overflow-auto w-full">
              <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-lg font-medium">Image Analysis Details</h3>
                <button
                  onClick={() => {
                    setAnalysisModal(false)
                    setSelectedAnalysis(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-lg font-mono leading-relaxed">
                  {selectedAnalysis}
                </pre>
              </div>
            </div>
          </div>
        )
      }

      {dermaModal && selectedDerma?.type === 'skin' && isSuperAdmin && (
        <SkinModelReviewModal
          isOpen={dermaModal}
          consultation={selectedDerma.consultation}
          onClose={() => {
            setDermaModal(false)
            setSelectedDerma(null)
          }}
          userRole={userRole}
          onReviewSaved={handleReviewSaved}
        />
      )}

      {/* Derma Output Modal */}
      {
        dermaModal && selectedDerma && selectedDerma.type !== 'skin' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-[85vh] overflow-auto w-full">
              <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-lg font-medium">Derma Output Details</h3>
                <button
                  onClick={() => {
                    setDermaModal(false)
                    setSelectedDerma(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                {(() => {
                  const rawOutput = selectedDerma.output
                  const parsed = parseDermaOutput(rawOutput)
                  if (parsed) {
                    // Beautiful structured display for JSON
                    return (
                      <div className="space-y-6">
                        {/* Categories */}
                        {parsed.CATEGORIES && (
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">📋 Categories</h4>
                            <p className="text-sm text-gray-900">{parsed.CATEGORIES}</p>
                          </div>
                        )}

                        {/* Analysis */}
                        {parsed.ANALYSIS && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">🔍 Analysis</h4>
                            <p className="text-sm text-gray-900 leading-relaxed">{parsed.ANALYSIS}</p>
                          </div>
                        )}

                        {/* Morning Routine */}
                        {parsed.morning_routine && parsed.morning_routine.length > 0 && (
                          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              🌅 Morning Routine
                            </h4>
                            <div className="space-y-2">
                              {parsed.morning_routine.map((item, idx) => (
                                <div key={idx} className="bg-white p-3 rounded-md border border-yellow-100">
                                  <div className="flex items-start gap-2">
                                    <span className="font-semibold text-orange-600 text-xs">{item.slot}:</span>
                                    <span className="text-sm text-gray-900">{item.search_query}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Evening Routine */}
                        {parsed.evening_routine && parsed.evening_routine.length > 0 && (
                          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              🌙 Evening Routine
                            </h4>
                            <div className="space-y-2">
                              {parsed.evening_routine.map((item, idx) => (
                                <div key={idx} className="bg-white p-3 rounded-md border border-purple-100">
                                  <div className="flex items-start gap-2">
                                    <span className="font-semibold text-purple-600 text-xs">{item.slot}:</span>
                                    <span className="text-sm text-gray-900">{item.search_query}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Raw JSON fallback for any other fields */}
                        <details className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <summary className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-gray-900">
                            📄 View Raw JSON
                          </summary>
                          <pre className="mt-3 text-xs text-gray-600 overflow-x-auto">
                            {JSON.stringify(parsed, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )
                  } else {
                    // Fallback to plain text display
                    return (
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-lg font-mono leading-relaxed">
                        {rawOutput}
                      </pre>
                    )
                  }
                })()}
              </div>
            </div>
          </div>
        )
      }

      {/* Outcome Selection Modal */}
      {
        outcomeModal && selectedConsultation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">Set Consultation Outcome</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Select the outcome for {selectedConsultation.name}'s consultation
                </p>
              </div>
              <div className="p-6 space-y-3">
                {outcomeOptions.map((option) => {
                  const Icon = option.icon
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleOutcomeUpdate(selectedConsultation.consultation_id, option.value)}
                      className={`w-full p-4 rounded-lg border-2 transition-all hover:border-purple-500 hover:shadow-md ${option.color} border-transparent`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Icon className="w-5 h-5" />
                          <span className="font-medium">{option.label}</span>
                        </div>
                        <span className="text-xs">
                          {option.value === 'paid' && '→ Goes to Shopify'}
                          {option.value === 'hot_lead' && '→ Follow up later'}
                          {option.value === 'cold_lead' && '→ Not interested'}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => {
                    setOutcomeModal(false)
                    setSelectedConsultation(null)
                  }}
                  className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
              {updateError && (
                <div className="px-6 pb-4">
                  <p className="text-sm text-red-600">{updateError}</p>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Assignment / Re-assignment Modal */}
      {
        reassignModal && selectedConsultationForReassign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedConsultationForReassign.assigned_pharmacist ? 'Re-assign' : 'Assign'} Consultation
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedConsultationForReassign.assigned_pharmacist
                    ? `Re-assign ${selectedConsultationForReassign.name}'s consultation to a different pharmacist`
                    : `Assign ${selectedConsultationForReassign.name}'s consultation to a pharmacist`
                  }
                </p>
                {selectedConsultationForReassign.assigned_pharmacist ? (
                  <p className="text-sm text-gray-600 mt-2">
                    <span className="font-semibold">Currently assigned to:</span> {selectedConsultationForReassign.assigned_pharmacist}
                  </p>
                ) : (
                  <p className="text-sm text-orange-600 mt-2">
                    <span className="font-semibold">Status:</span> Currently unassigned
                  </p>
                )}
              </div>

              <div className="p-6 space-y-4">
                {/* Pharmacist Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Pharmacist
                  </label>
                  <select
                    value={selectedPharmacist}
                    onChange={(e) => setSelectedPharmacist(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={reassignLoading}
                  >
                    <option value="">-- Select Pharmacist --</option>
                    {availablePharmacists.map(pharmacist => {
                      const isOffline = !pharmacist.is_online
                      const isAtLimit = pharmacist.is_at_limit
                      const warning = isOffline ? '(OFFLINE)' : isAtLimit ? '(AT LIMIT)' : ''
                      const warningColor = isOffline ? '🔴' : isAtLimit ? '🟡' : '🟢'

                      return (
                        <option
                          key={pharmacist.username}
                          value={pharmacist.username}
                        >
                          {warningColor} {pharmacist.username} {warning} ({pharmacist.daily_counter}/{pharmacist.daily_limit})
                        </option>
                      )
                    })}
                  </select>
                </div>

                {/* Warning Messages */}
                {reassignSessionWarning && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <p className="text-sm text-amber-800 font-medium">⚠️ Session warning</p>
                    <p className="text-xs text-amber-700 mt-1">{reassignSessionWarning}</p>
                  </div>
                )}

                {selectedPharmacist && (
                  (() => {
                    const selected = availablePharmacists.find(p => p.username === selectedPharmacist)
                    if (!selected) return null

                    if (!selected.is_online) {
                      return (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-sm text-red-800 font-medium">⚠️ Warning: This pharmacist is currently OFFLINE</p>
                          <p className="text-xs text-red-600 mt-1">They may not be available to process this consultation immediately.</p>
                        </div>
                      )
                    }

                    if (selected.is_at_limit) {
                      return (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                          <p className="text-sm text-yellow-800 font-medium">⚠️ Warning: This pharmacist has reached their daily limit</p>
                          <p className="text-xs text-yellow-600 mt-1">Current: {selected.daily_counter}/{selected.daily_limit} consultations today</p>
                        </div>
                      )
                    }

                    return (
                      <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <p className="text-sm text-green-800">✓ This pharmacist is available</p>
                        <p className="text-xs text-green-600 mt-1">Online • {selected.daily_counter}/{selected.daily_limit} consultations today</p>
                      </div>
                    )
                  })()
                )}

                {/* Error Display */}
                {reassignError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-600">{reassignError}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-6 border-t bg-gray-50 flex gap-3">
                <button
                  onClick={() => {
                    setReassignModal(false)
                    setSelectedConsultationForReassign(null)
                    setSelectedPharmacist('')
                    setReassignError('')
                    setReassignSessionWarning('')
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={reassignLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassign}
                  className={`flex-1 px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${selectedConsultationForReassign.assigned_pharmacist
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-green-600 hover:bg-green-700'
                    }`}
                  disabled={reassignLoading || !selectedPharmacist}
                >
                  {reassignLoading
                    ? (selectedConsultationForReassign.assigned_pharmacist ? 'Re-assigning...' : 'Assigning...')
                    : (selectedConsultationForReassign.assigned_pharmacist ? 'Confirm Re-assign' : 'Confirm Assign')
                  }
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  )
}

export default ConsultationsTable
